// API helper for the Costs SPA.
// Internal-Dashboard stores the JWT in localStorage under 'access_token'
// (persists across tabs and browser restarts).
// Every request to /api/costs/* must include `Authorization: Bearer <jwt>`.

const TOKEN_KEY = 'access_token';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function api<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`/api/costs${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    window.location.href = '/signin';
    throw new Error('unauthorized');
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const err = new Error('forbidden') as Error & { code?: string; required?: string };
    err.code = body?.error?.code;
    err.required = body?.error?.required;
    throw err;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.code ?? `request_failed_${res.status}`);
  }
  return res.json() as Promise<T>;
}

// Same auth + error handling as api(), but mounts at /api/pricing instead of /api/costs.
export async function apiPricing<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`/api/pricing${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (res.status === 401) {
    window.location.href = '/signin';
    throw new Error('unauthorized');
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const err = new Error('forbidden') as Error & { code?: string; required?: string };
    err.code = body?.error?.code;
    err.required = body?.error?.required;
    throw err;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `request_failed_${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ───────────────────────────── Types ─────────────────────────────

export interface ChartSeries {
  name: string;
  data: number[];
}

export type ChartData =
  | { labels: string[]; series: ChartSeries[] }
  | { month: string; kpis: Array<{ label: string; value: number; unit: string }> }
  | { columns: string[]; rows: Array<Record<string, unknown>> };

export interface ChartPayload {
  key: string;
  type: 'bar' | 'line' | 'stacked-bar' | 'kpi' | 'table';
  title: string;
  data: ChartData;
  generatedAt: string;
}

export interface MeResponse {
  email: string;
  role: string | null;
  permissions: string[];
  accessAdmin?: boolean;
  scope?: string; // 'full' | 'product' | 'techops' | 'growth' — team data scope
}

// ── Access management (admin-only; /api/admin/*) ──────────────────────────────
export interface AccessUser {
  email: string;
  team: string;
  role: string;
  active: boolean;
  added_by: string;
  added_at: string | null;
  last_login: string | null;
}
export interface AccessTeam { name: string; scope: string[]; description: string; }
export interface AccessData {
  users: AccessUser[];
  teams: AccessTeam[];
  domains: string[];
  roles: string[];
}

export async function apiAdmin<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`/api/admin${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (res.status === 401) { window.location.href = '/signin'; throw new Error('unauthorized'); }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || body?.error?.code || `request_failed_${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const getAccess = () => apiAdmin<AccessData>('/access');
export const putAccessUser = (b: { email: string; team?: string; role?: string; active?: boolean }) =>
  apiAdmin<{ ok: boolean; user: AccessUser }>('/access/users', { method: 'POST', body: JSON.stringify(b) });
export const deleteAccessUser = (email: string) =>
  apiAdmin<{ ok: boolean }>(`/access/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
export const putAccessTeam = (name: string, b: { scope: string[]; description?: string }) =>
  apiAdmin<{ ok: boolean; team: AccessTeam }>(`/access/teams/${encodeURIComponent(name)}`, { method: 'PUT', body: JSON.stringify(b) });

export interface LatestMonthResponse {
  month: string | null; // 'YYYY-MM'
}

export interface AvailableMonthsResponse {
  months: string[]; // ['YYYY-MM', ...] ascending
}

export interface ChartSqlResponse {
  key: string;
  title: string;
  page: string;
  type: string;
  permission: string | null;
  sql: string;
}
